import { sendEventMetric, withMetrics } from "@expert/cloudwatch-metrics";
import { getLogger } from "@expert/logging";
import { useVerifiedCustomerStore } from "@expert/sales-tools";

import { useSessionStore } from "@/sdk/sessions/session.store";

import { linkCustomerToLine } from "./api/interactions";
import { getC360Line, metricDimensions } from "./c360InteractionLifecycle";

const logger = getLogger({ module: "c360" });

// A confirmed number can be +1 and 10 digits (from the call) or the 10 digits the expert typed.
const US_MDN = /^(?:\+1)?(\d{10})$/;

/**
 * Links the session's C360 line to the customer the expert confirmed, so the call shows in that customer's C360
 * history. Called when the expert confirms the customer (`onCustomerConfirmed` in the sales context). Uses the
 * confirmed MDN and billing account number, not the caller's number. Never throws.
 */
export async function linkC360Customer(sessionId: string): Promise<void> {
    try {
        // Read before waiting for the line: if the session ends meanwhile, ending it clears the confirmed customer.
        const partner = useSessionStore.getState().getSession(sessionId)?.currentTask?.partner;
        const { customer } = useVerifiedCustomerStore.getState().getVerifiedCustomer(sessionId);
        const mdn = customer?.mdn && US_MDN.exec(customer.mdn)?.[1];
        // Sales' lookup calls the billing account number `accountNumber`; C360 calls it `billing_account_number`.
        const billingAccountNumber = customer?.accountNumber?.trim();
        if (!partner || !mdn || !billingAccountNumber) {
            logger.info(
                { sessionId, c360LinkSkipped: !partner ? "no_partner" : !mdn ? "no_mdn" : "no_billing_account_number" },
                "C360 customer link skipped",
            );
            return;
        }
        const line = await getC360Line(sessionId);
        // No line: recording is off, or the start failed. Debug, not info: it happens on every chat confirm and every
        // call with recording off, while the other skips are confirmed customers we couldn't link.
        if (!line) {
            logger.debug({ sessionId, c360LinkSkipped: "no_line" }, "C360 customer link skipped");
            return;
        }
        const result = await withMetrics(
            "C360LinkCustomer",
            () =>
                linkCustomerToLine({
                    sessionId,
                    partner,
                    interactionLineId: line.interactionLineId,
                    mdn,
                    billingAccountNumber,
                }),
            { logger, rethrow: false, logResult: false, dimensions: metricDimensions(partner) },
        );
        // withMetrics logs the error without the session, so this ties the failure to the call.
        if (!result) {
            logger.warn({ sessionId, partner, interactionLineId: line.interactionLineId }, "C360 customer link failed");
            return;
        }
        void sendEventMetric("C360LinkCustomer", { ...metricDimensions(partner), outcome: result.outcome });
        logger.info(
            { sessionId, partner, interactionLineId: line.interactionLineId, c360LinkOutcome: result.outcome },
            "C360 customer link",
        );
    } catch (error) {
        logger.warn({ error, sessionId }, "C360 customer link failed");
    }
}
